import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Spin } from 'antd';
import axios from 'axios';

import FullPhoto from '../../common/photos/FullPhoto';
import { constructCompletedFormPDF, decorateFile } from '../formHelpers';

import { getIdMap } from '../../helpers/helpers';
import { addFileToFileMap } from '../../files/state/files.actions';

/** PDF Preview Section for Completed Forms */
function PDFPreview({
  sliderVisible,
  formData,
  selectedForm,
  visible,
}) {
  const dispatch = useDispatch();

  const {
    company: {
      settings = {},
      companyImageURL: logo,
    } = {},
  } = useSelector((state) => state.settings);
  const users = useSelector((state) => state.users.users);
  const projects = useSelector((state) => state.projects.projects);
  const equipment = useSelector((state) => state.equipment.equipment);
  const globalFileMap = useSelector((state) => state.files.fileMap);

  const [PDF, setPDF] = useState();
  const [loading, setLoading] = useState(false);

  const userMap = useMemo(() => getIdMap(users), [users]);
  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const equipmentIdMap = useMemo(() => getIdMap(equipment), [equipment]);

  useEffect(() => {
    const getAndSetPDF = async () => {
      const {
        templateName,
        userId,
        createdAt = 0,
        submittedAt,
        data,
        employeeSignature,
        fileMap = {},
        drawOptions = [],
        useStandardTemplate,
        pdfVersion,
        number: formNumber,
        templateSchema,
      } = selectedForm || {};
      const {
        collected = {},
      } = data || {};

      setLoading(true);

      const loadedFiles = await Promise.all(
        Object.keys(fileMap).map(async (fileId) => {
          const globalFile = globalFileMap[fileId];
          if (globalFile) {
            fileMap[fileId].jsFileObject = globalFile;
            return null;
          }
          const { data: signedUrl } = await axios.get(`/download/file/${fileId}`);
          fileMap[fileId].url = signedUrl;
          return decorateFile(fileMap[fileId], fileId);
        }),
      );

      loadedFiles.forEach((file) => {
        if (!file) return;
        fileMap[file.id].jsFileObject = file;
        dispatch(addFileToFileMap(file.id, file));
      });

      const {
        [userId]: {
          name: submitterName,
          employeeId,
        } = {},
      } = userMap;

      const generatedPDF = await constructCompletedFormPDF({
        title: formNumber ? `${templateName} - ${formNumber}` : templateName,
        useStandardTemplate,
        pdfVersion,
        formData,
        fileMap,
        data,
        createdAt,
        submittedAt,
        drawOptions,
        settings,
        projectIdMap,
        equipmentIdMap,
        logo,
        employeeSignature,
        submitterName,
        employeeId,
        collected,
        number: formNumber,
        templateSchema,
      });
      setPDF(generatedPDF);
      setLoading(false);
    };
    if (sliderVisible) {
      getAndSetPDF();
    }
  }, [sliderVisible, formData, settings, logo, userMap, projectIdMap, equipmentIdMap]);

  useEffect(() => {
    if (!sliderVisible) {
      setPDF();
      setLoading(false);
    }
  }, [sliderVisible]);

  return (
    <>
      {
        visible ? (
          <FullPhoto
            file={PDF}
            type="pdf"
            className="completed-form-contained"
          />
        ) : null
      }
      {loading
        && (
        <Row justify="center" align="middle" className="form-loading-container">
          <Spin size="large" />
        </Row>
        )}
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
PDFPreview.propTypes = {
  sliderVisible: PropTypes.bool,
  formData: PropTypes.array,
  selectedForm: PropTypes.object,
  visible: PropTypes.bool.isRequired,
};

PDFPreview.defaultProps = {
  sliderVisible: false,
  formData: [],
  selectedForm: {},
};

export default PDFPreview;
